import { ParkingaboLogoHeaderSmall } from 'parkingabo-shared/layout/ParkingaboLogoHeader.tsx';
import { useTheme } from '@mui/material/styles';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useParamOrNull } from 'dg-web-shared/lib/ReactRouterHelpers.ts';
import { ParkingaboAsyncLoadedSection } from '../../components/layout/ParkingaboAsyncLoadedSection.tsx';
import {
    ParkingaboUserBadgeType,
    ParkingaboVehicle,
    VehicleLicensePlateType,
} from 'dg-web-shared/common/models/Vehicle.tsx';
import {
    Box,
    Button,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { DriveEta, Person } from '@mui/icons-material';
import {
    Localized,
    useLanguage,
    useTranslation,
} from 'dg-web-shared/lib/Localization.tsx';
import {
    CustomerTenantLegalForm,
    getCustomerDisplayName,
} from 'dg-web-shared/common/models/Users.tsx';
import { Icon16 } from 'dg-web-shared/ui/icons/Icon.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { RoutedModal } from 'dg-web-shared/common/components/material-ui/routed-modal/RoutedModal.tsx';
import { ParkingaboLayoutWithHeader } from '../../components/layout/ParkingaboLayoutWithHeader.tsx';
import { DiscardChangesDialog } from '../../components/layout/ParkingaboRoutedModal.tsx';
import {
    makeCountryOptions,
    useCountryFetch,
} from '../../hooks/UseCountryFetch.tsx';
import { useMemo, useState } from 'react';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';

import {
    ReactHookFormDropdownSelect,
    ReactHookFormSelectOption,
} from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSelect.tsx';
import { mandatoryFieldErrorMessage } from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import Grid from '@mui/material/Grid';
import { licensePlateOptions } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/CommonOptions.tsx';
import { getDisplayLabelForBadge } from 'product-shared/tenant/TenantVehicleIdentificationUtils.tsx';
import { UseFormReturn, useForm } from 'react-hook-form';
import { FeedbackPopup } from '../../components/FeedbackPopup.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { useRequestErrorFieldMap } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import { VehicleIdentification } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { ParkingaboContact } from 'product-shared/parkingabo/ParkingaboContact.ts';
import { ParkingaboContactInfo } from '../Help.tsx';

type ExternalVehiclesResponse = {
    customer: Customer;
    operatorName: string;
    parkingaboContact: ParkingaboContact | null;
    vehicleIdentification: VehicleIdentification[];
    vehicles: ParkingaboVehicle[];
};

type Customer = {
    firstName: string | null;
    lastName: string | null;
    company1: string | null;
    company2: string | null;
    legalForm: CustomerTenantLegalForm;
};

export function ExternalVehiclesRoute() {
    const theme = useTheme();

    const externalVehicleUrlToken = useParamOrNull('externalVehicleUrlToken');
    const [externalVehiclesState, refetchExternalVehicles] = useServerFetch<
        ExternalVehiclesResponse,
        { externalVehicleUrlToken: string | null }
    >(
        ({ externalVehicleUrlToken }) => ({
            url: `/ui-api/parkingabo/external-vehicles/${externalVehicleUrlToken}/`,
        }),
        { externalVehicleUrlToken },
    );

    return (
        <div
            style={{
                backgroundColor: theme.palette.grey.A200,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'start',
            }}
        >
            <ParkingaboLogoHeaderSmall />
            <ParkingaboAsyncLoadedSection
                state={externalVehiclesState}
                render={data => (
                    <>
                        <ExternalVehicles
                            customer={data.customer}
                            operatorName={data.operatorName}
                            vehicles={data.vehicles}
                        />
                        <ExternalVehiclesOutlet
                            context={{
                                operatorName: data.operatorName,
                                parkingaboContact: data.parkingaboContact,
                                vehicleIdentification:
                                    data.vehicleIdentification,
                                vehicles: data.vehicles,
                                onVehicleUpdated: refetchExternalVehicles,
                            }}
                        />
                    </>
                )}
            />
        </div>
    );
}

export function useExternalVehiclesContext() {
    return useOutletContext<ExternalVehiclesOutletContext>();
}

function ExternalVehiclesOutlet({
    context,
}: {
    context: ExternalVehiclesOutletContext;
}) {
    return <Outlet context={context} />;
}

type ExternalVehiclesOutletContext = {
    operatorName: string;
    parkingaboContact: ParkingaboContact | null;
    vehicleIdentification: VehicleIdentification[];
    vehicles: ParkingaboVehicle[];
    onVehicleUpdated: () => void;
};

function ExternalVehicles({
    customer,
    operatorName,
    vehicles,
}: {
    customer: Customer;
    operatorName: string;
    vehicles: ParkingaboVehicle[];
}) {
    return (
        <>
            <Box sx={{ marginY: 3 }}>
                <Typography variant="h1">{operatorName}</Typography>
            </Box>
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    backgroundColor: 'whitesmoke.main',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflowY: 'auto',
                }}
            >
                <Container
                    disableGutters={true}
                    sx={{
                        flexGrow: 1,
                        height: '100%',
                        paddingBottom: '24px',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'common.white',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <VehiclesHeader customer={customer} />
                        <VehiclesTable vehicles={vehicles} />
                    </Box>
                </Container>
            </Box>
        </>
    );
}

function VehiclesHeader({ customer }: { customer: Customer }) {
    const theme = useTheme();

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '15px 25px',
                }}
            >
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <DriveEta
                        style={{
                            fontSize: 42,
                            color: theme.palette.primary.main,
                            marginRight: theme.spacing(2),
                        }}
                    />
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: 26,
                        }}
                    >
                        <Localized
                            de="Fahrzeuge"
                            fr="Véhicules"
                            it="Veicoli"
                            en="Vehicles"
                        />
                    </Typography>
                </Box>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {/* TODO headerButtons */}
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.getContrastText(
                        theme.palette.primary.main,
                    ),
                    padding: theme.spacing(3 / 8, 3),
                }}
            >
                <Person />
                <Typography style={{ fontSize: 16, marginLeft: 4 }}>
                    {getCustomerDisplayName(customer, false)}
                </Typography>
            </Box>
        </>
    );
}

function LicensePlateIcon({
    licensePlateType,
}: {
    licensePlateType: VehicleLicensePlateType;
}) {
    switch (licensePlateType) {
        case VehicleLicensePlateType.CAR:
            return <Icon16 icon={Icons16.licensePlateType.car} />;
        case VehicleLicensePlateType.MOTORCYCLE:
            return <Icon16 icon={Icons16.licensePlateType.motorcycle} />;
    }
}

function VehiclesTable({ vehicles }: { vehicles: ParkingaboVehicle[] }) {
    const navigate = useNavigate();

    if (vehicles.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginY: 6,
                }}
            >
                <Typography variant="subtitle1">
                    <Localized
                        de="Keine Fahrzeuge"
                        fr="Aucun véhicule"
                        it="Nessun veicolo"
                        en="No vehicles"
                    />
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Bezeichnung"
                                fr="Description"
                                it="Descrizione"
                                en="Description"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Kennzeichen"
                                fr="Immatriculation"
                                it="Numero di targa"
                                en="License plate"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Badge"
                                fr="Badge"
                                it="Badge"
                                en="Badge"
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vehicles.map(vehicle => (
                        <TableRow
                            key={vehicle.customerTenantCarId}
                            hover
                            onClick={() => {
                                navigate(
                                    `vehicles/${vehicle.customerTenantCarId}`,
                                );
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell>{vehicle.description}</TableCell>
                            <TableCell>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    {vehicle.type && (
                                        <LicensePlateIcon
                                            licensePlateType={vehicle.type}
                                        />
                                    )}
                                    {vehicle.licensePlateNr
                                        ? vehicle.licensePlateNr
                                        : EmDash}
                                    {vehicle.country && ` (${vehicle.country})`}
                                </Box>
                            </TableCell>
                            <TableCell>
                                {vehicle.badgeLabelNr
                                    ? addSpacesEveryFourthFromEnd(
                                          vehicle.badgeLabelNr,
                                      )
                                    : EmDash}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function addSpacesEveryFourthFromEnd(input: string): string {
    return input.replace(/(.)(?=(.{4})+$)/g, '$1 ');
}

export type VehiclePayload = {
    description: string;
    licensePlateNr: string | null;
    country: string | null;
    type: VehicleLicensePlateType | null;
};

export function ExternalVehiclesDetailModal() {
    const navigate = useNavigate();

    const externalVehicleUrlToken = useParamOrNull('externalVehicleUrlToken');
    const customerTenantCarId = useParamOrNull('customerTenantCarId');
    const { vehicleIdentification, vehicles, onVehicleUpdated } =
        useExternalVehiclesContext();

    const vehicle = useMemo(() => {
        return vehicles.find(
            v => v.customerTenantCarId === customerTenantCarId,
        );
    }, [vehicles, customerTenantCarId]);

    const [requestState, sendRequest] = useServerWrite<
        VehiclePayload,
        null,
        ValidationData
    >(() => ({
        url: `/ui-api/parkingabo/external-vehicles/${externalVehicleUrlToken}/vehicles/${customerTenantCarId}`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(requestState, () => {
        onVehicleUpdated();
        navigate('..');
    });

    const [countriesState] = useCountryFetch();

    if (!externalVehicleUrlToken || !vehicle) {
        return (
            <FeedbackPopup
                open={true}
                onConfirm={() => {
                    navigate('..');
                }}
                title={
                    <Localized
                        de="Fahrzeug nicht gefunden"
                        fr="Véhicule non trouvé"
                        it="Veicolo non trovato"
                        en="Vehicle not found"
                    />
                }
            >
                <Localized
                    de="Das Fahrzeug konnte nicht gefunden werden."
                    fr="Le véhicule est introuvable."
                    it="Il veicolo non è stato trovato."
                    en="The vehicle could not be found."
                />
            </FeedbackPopup>
        );
    }

    return (
        <RoutedModal
            open={true}
            style={{
                maxWidth: 500,
                maxHeight: 500,
            }}
            render={() => (
                <ParkingaboLayoutWithHeader
                    backTo={'..'}
                    title={
                        <Localized
                            de="Fahrzeugdetails"
                            fr="Détails du véhicule"
                            it="Dettagli del veicolo"
                            en="Vehicle details"
                        />
                    }
                >
                    <ParkingaboAsyncLoadedSection
                        state={countriesState}
                        render={data => {
                            return (
                                <VehicleForm
                                    countriesOptions={makeCountryOptions(data)}
                                    requestState={requestState}
                                    submit={sendRequest}
                                    vehicle={vehicle}
                                    isLpRequired={vehicleIdentification.includes(
                                        VehicleIdentification.LICENSE_PLATE,
                                    )}
                                />
                            );
                        }}
                    />
                </ParkingaboLayoutWithHeader>
            )}
            DiscardChangesDialog={DiscardChangesDialog}
        />
    );
}

enum Fields {
    Description = 'description',
    LicensePlateNr = 'licensePlateNr',
    Country = 'country',
    LicensePlateType = 'type',
}

function VehicleForm({
    requestState,
    submit,
    countriesOptions,
    vehicle,
    isLpRequired,
}: {
    requestState: ServerRequestState<null, ValidationData>;
    submit: (formData: VehiclePayload) => void;
    countriesOptions: ReactHookFormSelectOption<string>[];
    vehicle: ParkingaboVehicle;
    isLpRequired: boolean;
}) {
    const { language } = useLanguage();
    const navigate = useNavigate();

    const form = useForm<VehiclePayload>({
        defaultValues: vehicle,
    });

    useRequestErrorFieldMap(requestState, form, language);

    const [showEditWithOpenEntryWarning, setShowEditWithOpenEntryWarning] =
        useState(false);
    const { operatorName, parkingaboContact } = useExternalVehiclesContext();

    const description = form.watch(Fields.Description);
    const licensePlateNr = form.watch(Fields.LicensePlateNr);
    const licensePlateType = form.watch(Fields.LicensePlateType);
    const country = form.watch(Fields.Country);
    const showLpFields = Boolean(licensePlateNr);

    function handleEditCancel() {
        form.reset();
        navigate(-1);
    }

    return (
        <Box
            style={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
            }}
        >
            <BackendRequestErrorMessage requestState={requestState} />
            <Box>
                <ReactHookFormTextField<VehiclePayload>
                    sx={{
                        marginBottom: 3,
                    }}
                    control={form.control}
                    name={Fields.Description}
                    required={true}
                    label={
                        <Localized
                            de="Beschreibung"
                            fr="Description"
                            it="Descrizione"
                            en="Description"
                        />
                    }
                />

                <LicensePlateFields
                    form={form}
                    showLpFields={showLpFields}
                    isLpRequired={isLpRequired}
                    countriesOptions={countriesOptions}
                    licensePlateType={licensePlateType}
                    country={country}
                />

                <BadgeFields
                    badgeLabelNr={vehicle.badgeLabelNr}
                    badgeType={vehicle.badgeType}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '8px',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleEditCancel}
                    sx={{ marginRight: 1 }}
                >
                    <Localized
                        de="Abbrechen"
                        fr="Annuler"
                        it="Annulla"
                        en="Cancel"
                    />
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    sx={{ marginLeft: 1 }}
                    disabled={
                        !form.formState.isDirty ||
                        description.length === 0 ||
                        (isLpRequired && !licensePlateNr)
                    }
                    loading={requestState.status === RequestStatus.PENDING}
                    onClick={() => {
                        if (
                            vehicle?.hasOpenEntryWithoutExit &&
                            (vehicle.licensePlateNr != licensePlateNr ||
                                vehicle.country != country ||
                                vehicle.type != licensePlateType)
                        ) {
                            setShowEditWithOpenEntryWarning(true);
                        } else {
                            form.handleSubmit(formData =>
                                submit({
                                    ...formData,
                                    licensePlateNr:
                                        formData.licensePlateNr || null,
                                }),
                            )();
                        }
                    }}
                >
                    <Localized
                        de="Speichern"
                        fr="Sauvegarder"
                        it="Salva"
                        en="Save"
                    />
                </Button>
                <FeedbackPopup
                    color={'error'}
                    open={showEditWithOpenEntryWarning}
                    onConfirm={() => {
                        setShowEditWithOpenEntryWarning(false);
                    }}
                    confirmLabel={<Localized de="OK" fr="OK" it="OK" en="OK" />}
                    title={
                        <Localized
                            de="Änderung nicht möglich"
                            fr="Modification pas possible"
                            it="Modifica non possibile"
                            en="Editing not possible"
                        />
                    }
                >
                    <>
                        <p>
                            <Localized
                                de="Für dieses Fahrzeug läuft derzeit ein Parkvorgang und kann daher nicht über den externen Link geändert werden."
                                fr="Une transaction est en cours pour ce véhicule et il ne peut donc pas être modifié via le lien externe."
                                it="Per questo veicolo c'é una transazione in corso e per questo non può essere modificato tramite il link esterno."
                                en="There is a transaction in progress for this vehicle, so it cannot be modified via the external link."
                            />
                        </p>
                        <p>
                            <Localized
                                de="Für weitere Informationen wenden Sie sich bitte an den Betreiber:"
                                fr="Pour plus d'informations, veuillez contacter l'exploitant:"
                                it="Per ulteriori chiarimenti contatti il gestore:"
                                en="For further information, please contact the operator:"
                            />
                        </p>
                        <p>
                            <ParkingaboContactInfo
                                operatorName={operatorName}
                                parkingaboContact={parkingaboContact}
                            />
                        </p>
                    </>
                </FeedbackPopup>
            </Box>
        </Box>
    );
}

function LicensePlateFields({
    form,
    showLpFields,
    isLpRequired,
    countriesOptions,
    licensePlateType,
    country,
}: {
    form: UseFormReturn<VehiclePayload>;
    showLpFields: boolean;
    isLpRequired: boolean;
    countriesOptions: ReactHookFormSelectOption<string>[];
    licensePlateType: VehicleLicensePlateType | null;
    country: string | null;
}) {
    const { language } = useLanguage();
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);

    return (
        <Grid container sx={{ marginBottom: 3 }}>
            <Grid size={5}>
                <ReactHookFormTextField<VehiclePayload>
                    control={form.control}
                    name={Fields.LicensePlateNr}
                    required={isLpRequired}
                    label={
                        <Localized
                            de="Kennzeichen"
                            fr="Immatriculation"
                            it="Targa"
                            en="License plate"
                        />
                    }
                    forceUppercase
                    onChange={event => {
                        if (event.target.value) {
                            if (!licensePlateType) {
                                form.setValue(
                                    Fields.LicensePlateType,
                                    VehicleLicensePlateType.CAR,
                                );
                            }
                            if (!country) {
                                form.setValue(Fields.Country, 'CH');
                            }
                        } else {
                            form.setValue(Fields.LicensePlateType, null);
                            form.setValue(Fields.Country, null);
                        }
                    }}
                />
            </Grid>

            {showLpFields && (
                <>
                    <Grid sx={{ paddingLeft: '10px', flexGrow: 1 }}>
                        <ReactHookFormDropdownSelect<VehiclePayload, string>
                            control={form.control}
                            name={Fields.Country}
                            rules={{
                                required: {
                                    value: isLpRequired,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            label={
                                <Localized
                                    de="Land"
                                    fr="Pays"
                                    it="Nazione"
                                    en="Country"
                                />
                            }
                            options={countriesOptions}
                        />
                    </Grid>
                    <Grid sx={{ paddingLeft: '10px', flexGrow: 1 }}>
                        <ReactHookFormDropdownSelect<
                            VehiclePayload,
                            VehicleLicensePlateType
                        >
                            control={form.control}
                            name={Fields.LicensePlateType}
                            rules={{
                                required: {
                                    value: isLpRequired,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            label={
                                <Localized
                                    de="Fahrzeugtyp"
                                    fr="Type de véhicule"
                                    it="Tipo veicolo"
                                    en="Vehicle type"
                                />
                            }
                            options={licensePlateOptions(language)}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}

function BadgeFields({
    badgeLabelNr,
    badgeType,
}: {
    badgeLabelNr: string | null;
    badgeType: ParkingaboUserBadgeType | null;
}) {
    const { language } = useLanguage();

    if (!badgeLabelNr) {
        return null;
    }

    return (
        <Grid container sx={{ marginY: 3 }}>
            <TextField
                label={
                    <Localized de="Badge" fr="Badge" it="Badge" en="Badge" />
                }
                value={getDisplayLabelForBadge(
                    language,
                    badgeLabelNr,
                    badgeType,
                )}
                disabled
                fullWidth
            />
        </Grid>
    );
}
